import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Column, Lookup } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { GroupItem, SimpleItem, Label, EmptyItem, FormRef } from "devextreme-react/form";
import ICache from "../../models/ICache";
import { FormListBase, FormListBaseTab, FormListBaseGrid, FormListBaseToolbar } from '../base';
import { TextBox, Button } from 'devextreme-react';
import useCustomDataSource, { Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import useClient from '../../components/selectbox/client';
import useLookUpBase from '../../components/selectbox/LookupBase';
import GestionSecteurs from '../gestion-secteurs/gestion-secteurs';
import GestionGroupesChasseurs from '../gestion-groupes-chasseurs/gestion-groupes-chasseurs';
import useEnum from '../../hooks/useEnum';
import './gestion-caches.css';
import Guid from '../../models/Guid';
import { usePays, useProvinces } from '../../components/selectbox';

// Custom hook to detect screen size
const useScreenSize = () => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenWidth;
};

export default function GestionCaches() {
  const screenWidth = useScreenSize();
  const cacheFormDataSource = useCustomDataSource<ICache, ICache>({
    path: "cache",
    primaryKey: "idCache"
  });
  const { selectedDataRow: selectedCache, setMainForm, mode, setMode, isFieldModifier, setIsFieldModifier, setSelectedDataRow, onFieldDataChanged } = cacheFormDataSource;
  const { editorOptions: secteurPecheEditorOption, PopupGestion: PopupGestionsecteurPeche } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "secteur", showClear: mode !== Mode.Lecture,
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idSecteur", Guid.empty.toString());
    }
  });
  const { editorOptions: groupeChasseursEditorOption, PopupGestion: PopupGestiongroupeChasseurs } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "groupeChasseur",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idGroupeChasseur", Guid.empty.toString());
    },
    showClear: mode !== Mode.Lecture, width: '50%'
  });
  const { clientEditorOptions, PopupGestionClient, currentClient } = useClient(true);
  const { editorOptions: statutEditorOptions } = useEnum(undefined, "gestionCacheStatutCache");
  const { editorOptions: etatCacheEditorOptions } = useEnum(undefined, "gestionCacheEtatCache");
  const formRef = useRef<FormRef>(null);
  const { provinces } = useProvinces();
  const [selectedProvince, setSelectedProvince] = useState<any>();
  const [latDegDec, setLatDegDec] = useState<number>(0);
  const [longDegDec, setLongDegDec] = useState<number>(0);

  const onSelectionchanged = (item: any, e: string) => {
    setIsFieldModifier(true);
    setSelectedDataRow((prev: ICache) => {
      return { ...prev, [e]: item.id }
    })
  }
  const nombrePancartesOptions = useMemo(() => {
    return {
      min: 0, max: 999, showSpinButtons: true, width: '65px',
      inputAttr: { style: 'text-align: right; padding-inline-end: 0px;' }
    };
  }, []);
  const dateEmissionEditorOptions = useMemo(() => {
    return {
      width: '50%',
      displayFormat: 'dd-MM-yyyy HH:mm',
    };
  }, []);
  const actifOptions = useMemo(() => {
    return { text: "Active" };
  }, []);
  const validerOptions = useMemo(() => {
    return { text: "À valider" };
  }, []);
  const droitAcquisOptions = useMemo(() => {
    return { text: "Droit acquis (Exception lors de la vérification de proximité)" };
  }, []);
  const remarquesOptions = useMemo(() => {
    return { height: 200, width: "80%" };
  }, []);
  const gpsEditorOptions = useMemo(() => {
    return { width: '65px' };
  }, []);
  const centeredEditorOptions = useMemo(() => {
    return {
      stylingMode: 'outlined',
      inputAttr: { style: 'text-align: center;' }
    };
  }, []);
  const secondsEditorOptions = useMemo(() => {
    return {
      stylingMode: 'outlined',
      format: "#,##0.000",
      inputAttr: { style: 'text-align: center;' }
    };
  }, []);
  const superficieEditorOptions = useMemo(() => {
    return {
      format: { type: 'fixedPoint', precision: 2 },
      inputAttr: { style: 'text-align: right; padding-inline-end: 0px;' }
    };
  }, []);
  const anneeEditorOptions = useMemo(() => {
    return {
      inputAttr: { style: 'text-align: right; padding-inline-end: 0px;' }
    };
  }, []);
  const latDegDecEditorOptions = useMemo(() => {
    return {
      value: latDegDec,
      stylingMode: 'outlined',
      readOnly: true,
      inputAttr: { style: 'text-align: center;' }
    };
  }, [latDegDec]);
  const longDegDecEditorOptions = useMemo(() => {
    return {
      value: longDegDec,
      stylingMode: 'outlined',
      readOnly: true,
      inputAttr: { style: 'text-align: center;' },
    };
  }, [longDegDec]);

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  })

  useEffect(() => {
    if (provinces && provinces.length > 0 && currentClient?.idProvince) {
      const foundProvince = provinces.find(p => p.idProvince?.equals(currentClient?.idProvince));
      if (foundProvince) {
        setSelectedProvince(foundProvince);
        console.log(foundProvince)
      }
    }
  }, [provinces, currentClient])

  useEffect(() => {
    if (selectedCache) {
      setLatDegDec(calculateLatitudeDecimal(selectedCache.latDeg, selectedCache.latMin, selectedCache.latSec));
      setLongDegDec(calculateLongitudeDecimal(selectedCache.longDeg, selectedCache.longMin, selectedCache.longSec));
    }
  }, [selectedCache])

  const DEG = 1.00000000;
  const MIN = 0.01666667;
  const SEC = 0.00027778;

  const calculateLatitudeDecimal = (latDeg: number = 0, latMin: number = 0, latSec: number = 0) => {
    const decimal = latDeg * DEG + (latMin * MIN) + (latSec * SEC);
    return decimal;
  }
  const calculateLongitudeDecimal = (longDeg: number = 0, longMin: number = 0, longSec: number = 0) => {
    const decimal = longDeg * DEG + (longMin * MIN) + (longSec * SEC);
    return decimal;
  }

  return (
    <FormListBase data={cacheFormDataSource}>
      <FormListBaseGrid dataSource={cacheFormDataSource.dataSource}
        afficherExtra={["latDegDec", "longDegDecNeg"]}
      >
        <Column key={"grid-col-id-permis"} dataField={"noCache"} caption="Numéro" allowHiding={false} />
        <Column key={"grid-col-prenom"} dataField={"idClient"} caption="Client" allowHiding={false}>
          <Lookup {...clientEditorOptions} />
        </Column>
        <Column key={"grid-col-date-emission"} dataField={"dateEmission"} caption="Date d'émission" allowHiding={false} />
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`Cache #${selectedCache?.noCache || ''}`} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} />
      <PopupGestionClient isModeSelection={mode !== Mode.Lecture} onSelectionchanged={(i) => onSelectionchanged(i, 'idClient')} />
      <PopupGestionsecteurPeche
        onSelectionchanged={(data: any) => {
          setIsFieldModifier(true);
          formRef.current?.instance().updateData("idSecteur", data.idSecteur);
        }}
      >
        <GestionSecteurs isModeSelection={true} />
      </PopupGestionsecteurPeche>
      <PopupGestiongroupeChasseurs
        onSelectionchanged={(data: any) => {
          setIsFieldModifier(true);
          formRef.current?.instance().updateData("idGroupeChasseur", data.idGroupeChasseur);
        }}
      >
        <GestionGroupesChasseurs isModeSelection={true} />
      </PopupGestiongroupeChasseurs>
      <FormListBaseTab>
        <TabItem title="Informations">
          <div style={{
            overflowY: 'auto', height: '100%',
          }}
          >
            <div style={{
              width: screenWidth > 1250 ? '60%' : '100%',
              overflowX: 'auto'
            }}>
              <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedCache || {} as ICache} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
                <GroupItem colCount={4}>
                  <GroupItem colSpan={3}>
                    <SimpleItem key={"frm-id-client"} dataField={"idClient"} editorOptions={clientEditorOptions} editorType={'dxSelectBox'}>
                      <Label showColon={false} text={"Client"} />
                    </SimpleItem>
                    {/* <SimpleItem key={"frm-id-secteur"} dataField={"noCache"}>
                <Label text={"Numéro de cache"} />
              </SimpleItem> */}
                    <SimpleItem key={"frm-id-secteur"} dataField={"idSecteur"} editorOptions={secteurPecheEditorOption} editorType={"dxSelectBox"}>
                      <Label showColon={false} text={"Secteur"} />
                    </SimpleItem>
                    <SimpleItem key={"frm-id-groupe-chasseur"} dataField={"idGroupeChasseur"} editorType={"dxSelectBox"}
                      editorOptions={
                        groupeChasseursEditorOption
                      }>
                      <Label showColon={false} text={"Groupe de chasseurs"} />
                    </SimpleItem>
                    <SimpleItem key={"frm-date-emission"} dataField={"dateEmission"}
                      editorType={"dxDateBox"}
                      editorOptions={
                        dateEmissionEditorOptions
                      }>
                      <Label showColon={false} text={"Date d'émission"} />
                    </SimpleItem>
                    <SimpleItem key={"frm-statut"} dataField={"statut"} editorType={"dxSelectBox"}
                      editorOptions={
                        statutEditorOptions
                      }>
                      <Label showColon={false} text={"Statut"} />
                    </SimpleItem>
                    <SimpleItem key={"frm-etat-cache"} dataField={"etatCache"} editorOptions={etatCacheEditorOptions} editorType={"dxSelectBox"}>
                      <Label showColon={false} text={"État"} />
                    </SimpleItem>
                    <GroupItem colCount={2}>
                      <GroupItem>
                        <SimpleItem key={"frm-superficie"} dataField={"superficie"} editorType={'dxNumberBox'} editorOptions={superficieEditorOptions}>
                          <Label showColon={false} text={"Superficie"} />
                        </SimpleItem>
                      </GroupItem>
                      <GroupItem>
                        <SimpleItem key={"frm-nombre-pancartes"} dataField={"nombrePancartes"} editorType={"dxNumberBox"}
                          editorOptions={
                            nombrePancartesOptions
                          }>
                          <Label showColon={false} text={"Nombre de pancartes"} />
                        </SimpleItem>
                      </GroupItem>
                    </GroupItem>
                    <GroupItem colCount={4}>
                      <GroupItem colSpan={2}>
                        <SimpleItem key={"frm-saison"} dataField={"annee"} editorOptions={anneeEditorOptions}>
                          <Label showColon={false} text={"Saison"} />
                        </SimpleItem>
                      </GroupItem>
                      <GroupItem colSpan={1}>
                        <SimpleItem key={"frm-actif"} dataField={"actif"} editorType={"dxCheckBox"}
                          editorOptions={
                            actifOptions
                          }
                        >
                          <Label visible={false} />
                        </SimpleItem>
                      </GroupItem>
                      <GroupItem colSpan={1}>
                        <SimpleItem key={"frm-a-valider"} dataField={"aValider"} editorType={"dxCheckBox"}
                          editorOptions={
                            validerOptions
                          }
                        >
                          <Label visible={false} />
                        </SimpleItem>
                      </GroupItem>
                    </GroupItem>
                    <SimpleItem key={"frm-droit-acquis"} dataField={"droitAcquis"} editorType={"dxCheckBox"}
                      editorOptions={
                        droitAcquisOptions
                      }
                    >
                      <Label visible={false} />
                    </SimpleItem>
                  </GroupItem>
                  {currentClient && (
                    <GroupItem>
                      <div>
                        <label>{currentClient.adresse}</label> <br />
                        <label>{currentClient.ville}, {selectedProvince?.description}</label> <br />
                        <label>{currentClient.codePostal}</label> <br />
                        <label>{currentClient.telephone1 || currentClient.telephone2 || currentClient.portable}</label> <br />
                        <br />
                        <label>Chasseur #: {currentClient.noChasseur}</label> <br />
                        <label>Membre #:{currentClient.matricule}</label>
                      </div>
                    </GroupItem>
                  )}
                </GroupItem>
              </Form>
            </div>
            <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedCache || {} as ICache} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
              <GroupItem caption={"Coordonnées GPS"}>
                <GroupItem cssClass="form-group" colCount={6}>
                  <GroupItem caption='Degrés' cssClass='text-align-right'>
                    <SimpleItem dataField="latDeg" editorType='dxNumberBox' editorOptions={{
                      ...centeredEditorOptions
                    }}>
                      <Label text={'Latitude'} />
                    </SimpleItem>
                    <SimpleItem dataField="longDeg" editorType='dxNumberBox' editorOptions={{
                      ...centeredEditorOptions
                    }}>
                      <Label text={'Longitude'} />
                    </SimpleItem>
                  </GroupItem>
                  <GroupItem caption='Minutes' cssClass='text-align-center'>
                    <SimpleItem dataField="latMin" editorType='dxNumberBox' editorOptions={{
                      ...centeredEditorOptions
                    }}>
                      <Label visible={false} />
                    </SimpleItem>
                    <SimpleItem dataField="longMin" editorType='dxNumberBox' editorOptions={{
                      ...centeredEditorOptions
                    }}>
                      <Label visible={false} />
                    </SimpleItem>
                  </GroupItem>
                  <GroupItem caption='Secondes' cssClass='text-align-center'>
                    <SimpleItem dataField="latSec" editorOptions={secondsEditorOptions} editorType='dxNumberBox'>
                      <Label visible={false} />
                    </SimpleItem>
                    <SimpleItem dataField="longSec" editorOptions={secondsEditorOptions} editorType='dxNumberBox'>
                      <Label visible={false} />
                    </SimpleItem>
                  </GroupItem>
                  <GroupItem caption='Degrés décimaux' cssClass='text-align-center'>
                    <SimpleItem dataField="latDegDec" editorOptions={latDegDecEditorOptions} editorType='dxNumberBox'>
                      <Label visible={false} />
                    </SimpleItem>
                    <SimpleItem dataField="longDegDecNeg" editorOptions={longDegDecEditorOptions} editorType='dxNumberBox'>
                      <Label visible={false} />
                    </SimpleItem>
                  </GroupItem>
                  <GroupItem cssClass='groupe-btn'>
                    <EmptyItem></EmptyItem>
                    <SimpleItem>
                      <Button text={"Vérifier la proximité"} />
                    </SimpleItem>
                    <SimpleItem dataField="test">
                      <Label visible={false} />
                    </SimpleItem>
                  </GroupItem>
                </GroupItem>
                <SimpleItem key={"frm-remarques"} dataField='notes' editorType={"dxTextArea"}
                  editorOptions={
                    remarquesOptions
                  }>
                  <Label text={"Remarques"} location={"top"} visible={true} />
                </SimpleItem>
              </GroupItem>
            </Form>
          </div>
        </TabItem>
        <TabItem title="Système"><FormSysteme formData={selectedCache || {} as ICache} /></TabItem>
      </FormListBaseTab>
    </FormListBase >
  );
}
